import { Link } from '@tanstack/react-router';
import { EyeIcon, LockIcon } from 'lucide-react';

import type { WorkspaceSummary } from '@/lib/api/generated/model';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import { IdentityTexture } from '@/shared/components/IdentityTexture';
import { accentFor } from '@/shared/components/identityAccent';
import { cn } from '@/lib/utils';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

import { workspaceInitial } from '../display';
import { workspaceMarkWords, workspaceMarks } from '../marks';

/** How many tiles the band holds from `sm`; a phone shows the first two. */
export const JUMP_BACK_IN_LIMIT = 4;

/**
 * Where this person was last, on this device (spec 199 US2): a shortcut, so it
 * must not look like more of the list below it. One line per workspace on a
 * tinted band — the name and when it was opened, nothing the grid already says.
 *
 * Private and View only stay, as icons whose words are in the link's name (spec
 * 196 US3); Owner does not, since a tile is about getting back, not about who
 * you are there.
 *
 * The band is `accent` in the light theme because `muted` there is the page's
 * own background and the band vanished into it; in dark, `muted` is a step up
 * from the page and reads as intended.
 */
export function JumpBackIn({
  visits,
  currentUserId,
}: {
  visits: { entry: WorkspaceSummary; openedAt: string }[];
  currentUserId: string | undefined;
}) {
  return (
    <section
      aria-labelledby="jump-back-in-heading"
      className="flex flex-col gap-2"
    >
      <EyebrowLabel>
        <span id="jump-back-in-heading">Jump back in</span>
      </EyebrowLabel>
      <ul className="bg-accent/60 dark:bg-muted/60 grid grid-cols-1 gap-2 rounded-xl p-2 sm:grid-cols-2 lg:grid-cols-4">
        {visits.map(({ entry, openedAt }, index) => {
          const marks = {
            ...workspaceMarks(entry, currentUserId),
            owner: false,
          };
          const opened = formatRelativeTime(openedAt);
          return (
            // Two on a phone: the band is a shortcut, and four stacked rows
            // there would push the list off the first screen again.
            <li key={entry.id} className={cn(index >= 2 && 'hidden sm:block')}>
              <Link
                to="/workspaces/$workspaceId"
                params={{ workspaceId: entry.id }}
                aria-label={[
                  entry.name,
                  ...workspaceMarkWords(marks),
                  `opened ${opened}`,
                ].join(', ')}
                title={entry.name}
                className="border-border bg-card hover:border-primary/40 hover:bg-accent/40 focus-visible:ring-ring touch-target flex items-center gap-2.5 rounded-lg border px-2.5 py-2 transition-[border-color,background-color] focus-visible:ring-3 focus-visible:outline-none motion-safe:duration-[var(--motion-duration-base)]"
              >
                {/* The grid card's monogram, smaller: the same workspace seen
                    twice. Not the same transition name, which only one element
                    may carry. */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'relative flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md text-xs font-semibold',
                    accentFor(entry.id),
                  )}
                >
                  <IdentityTexture id={entry.id} />
                  <span className="relative">
                    {workspaceInitial(entry.name)}
                  </span>
                </span>
                <span className="text-foreground min-w-0 flex-1 truncate text-sm font-medium">
                  {entry.name}
                </span>
                {marks.private ? (
                  <LockIcon
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className="text-muted-foreground size-3.5 shrink-0"
                  />
                ) : null}
                {marks.viewOnly ? (
                  <EyeIcon
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className="text-muted-foreground size-3.5 shrink-0"
                  />
                ) : null}
                <time
                  dateTime={openedAt}
                  title={formatExactTime(openedAt)}
                  className="text-muted-foreground shrink-0 text-xs tabular-nums"
                >
                  {opened}
                </time>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
